import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { RecentSalesPanels } from "./RecentSalesPanels";

export const HistoryTab = ({
  historyStats,
  loadingHistory,
  fetchHistoryStats,
  productId,
}) => {
  const [activityFilter, setActivityFilter] = useState("ALL");
  const [activitySearch, setActivitySearch] = useState("");

  const parseDateValue = (value) => {
    if (!value) return new Date(0);
    const asDate = new Date(value);
    if (!Number.isNaN(asDate.getTime())) return asDate;
    return new Date(0);
  };

  const formatDateTime = (value) => {
    if (!value) return "N/A";
    const date = parseDateValue(value);
    if (Number.isNaN(date.getTime()) || date.getTime() === 0) return String(value);
    return date.toLocaleString();
  };

  const toCurrency = (value) => `R ${(value || 0).toFixed(2)}`;

  const timelineEvents = useMemo(() => {
    const source = Array.isArray(historyStats?.timeline_events) ? historyStats.timeline_events : [];
    return [...source].sort((a, b) => parseDateValue(b?.date) - parseDateValue(a?.date));
  }, [historyStats?.timeline_events]);

  const availableActivityTypes = useMemo(() => {
    const uniqueTypes = [...new Set(timelineEvents.map((event) => event?.event_type).filter(Boolean))];
    return ["ALL", ...uniqueTypes];
  }, [timelineEvents]);

  const filteredTimeline = useMemo(() => {
    const query = activitySearch.trim().toLowerCase();
    return timelineEvents.filter((event) => {
      if (activityFilter !== "ALL" && event?.event_type !== activityFilter) {
        return false;
      }
      if (!query) return true;

      const searchable = [
        event?.event_type,
        event?.event_group,
        event?.reference,
        event?.status,
        event?.notes,
        event?.meta?.supplier,
        event?.meta?.transaction_type,
        event?.meta?.customer_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [timelineEvents, activityFilter, activitySearch]);

  if (loadingHistory) {
    return (
      <div className="flex justify-center items-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!historyStats) {
    return (
      <div className="text-center py-8 text-gray-500" data-testid="history-no-data">
        <p>No history data available</p>
        {fetchHistoryStats && productId && (
          <button
            onClick={() => fetchHistoryStats(productId)}
            className="mt-3 text-sm text-blue-600 underline hover:text-blue-800"
            data-testid="history-retry-btn"
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* Monthly Stats */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-blue-800 mb-3 flex items-center gap-2">
            <span className="text-lg">📊</span> This Month ({historyStats.monthly_stats?.period || 'Current Month'})
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Sales Amount (Incl VAT):</span>
              <span className="font-semibold text-blue-700">R {(historyStats.monthly_stats?.amount_incl_vat || historyStats.monthly_stats?.sales_amount_incl_vat || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Units Sold:</span>
              <span className="font-semibold">{historyStats.monthly_stats?.units || historyStats.monthly_stats?.units_sold || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Discount Given:</span>
              <span className="font-semibold text-orange-600">R {(historyStats.monthly_stats?.discount || historyStats.monthly_stats?.discount_given || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t pt-2 mt-2">
              <span className="text-gray-600">Gross Profit:</span>
              <span className={`font-bold ${(historyStats.monthly_stats?.gross_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                R {(historyStats.monthly_stats?.gross_profit || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>Transactions:</span>
              <span>{historyStats.monthly_stats?.transaction_count || 0}</span>
            </div>
          </div>
        </div>

        {/* YTD Stats */}
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <h3 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
            <span className="text-lg">📈</span> Year to Date ({historyStats.ytd_stats?.period || 'Current Year'})
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Sales Amount (Incl VAT):</span>
              <span className="font-semibold text-green-700">R {(historyStats.ytd_stats?.amount_incl_vat || historyStats.ytd_stats?.sales_amount_incl_vat || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Units Sold:</span>
              <span className="font-semibold">{historyStats.ytd_stats?.units || historyStats.ytd_stats?.units_sold || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Discount Given:</span>
              <span className="font-semibold text-orange-600">R {(historyStats.ytd_stats?.discount || historyStats.ytd_stats?.discount_given || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t pt-2 mt-2">
              <span className="text-gray-600">Gross Profit:</span>
              <span className={`font-bold ${(historyStats.ytd_stats?.gross_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                R {(historyStats.ytd_stats?.gross_profit || 0).toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-xs text-gray-500">
              <span>Transactions:</span>
              <span>{historyStats.ytd_stats?.transaction_count || 0}</span>
            </div>
          </div>
        </div>
      </div>
      <RecentSalesPanels recent={historyStats.recent_monthly_breakdown || []} imported={historyStats.imported_monthly_breakdown || []} />

      {/* Last Sold Date */}
      {historyStats.product_last_sold_date && (
        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 flex justify-between items-center">
          <span className="text-sm text-blue-700 font-medium">📅 Product Last Sold Date:</span>
          <span className="font-semibold text-blue-800">{historyStats.product_last_sold_date}</span>
        </div>
      )}

      {/* Document History */}
      <div className="bg-gray-50 p-4 rounded-lg border">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <span className="text-lg">📄</span> Document History
        </h3>
        
        {historyStats.last_document && (
          <div className="bg-purple-100 p-3 rounded mb-3 border border-purple-200">
            <div className="text-xs text-purple-600 font-medium">LAST DOCUMENT (NEWEST)</div>
            <div className="flex justify-between items-center mt-1">
              <span className="font-semibold text-purple-800">{historyStats.last_document.type}: {historyStats.last_document.doc_number || 'N/A'}</span>
              <span className="text-sm text-gray-600">
                {historyStats.last_document.date ? new Date(historyStats.last_document.date).toLocaleDateString() : 'N/A'}
              </span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm">
          {/* Last Sale */}
          <div className="bg-white p-3 rounded border">
            <div className="text-xs text-gray-500 font-medium mb-1">LAST SALE</div>
            {historyStats.last_sale ? (
              <>
                <div className="font-semibold">{historyStats.last_sale.doc_number || 'N/A'}</div>
                <div className="text-gray-600 text-xs">
                  {historyStats.last_sale.date ? new Date(historyStats.last_sale.date).toLocaleDateString() : 'N/A'}
                </div>
                <div className="text-xs mt-1">
                  Qty: {historyStats.last_sale.quantity} | R {(historyStats.last_sale.amount || 0).toFixed(2)}
                </div>
              </>
            ) : (
              <div className="text-gray-400 italic">No sales recorded</div>
            )}
          </div>

          {/* Last GRV */}
          <div className="bg-white p-3 rounded border">
            <div className="text-xs text-gray-500 font-medium mb-1">LAST GRV</div>
            {historyStats.last_grv ? (
              <>
                <div className="font-semibold">{historyStats.last_grv.grv_number || 'N/A'}</div>
                <div className="text-gray-600 text-xs">
                  {historyStats.last_grv.date ? new Date(historyStats.last_grv.date).toLocaleDateString() : 'N/A'}
                </div>
                <div className="text-xs mt-1 text-gray-500">{historyStats.last_grv.supplier || ''}</div>
              </>
            ) : (
              <div className="text-gray-400 italic">No GRV recorded</div>
            )}
          </div>

          {/* Last PO */}
          <div className="bg-white p-3 rounded border">
            <div className="text-xs text-gray-500 font-medium mb-1">LAST PURCHASE ORDER</div>
            {historyStats.last_po ? (
              <>
                <div className="font-semibold">{historyStats.last_po.po_number || 'N/A'}</div>
                <div className="text-gray-600 text-xs">
                  {historyStats.last_po.date ? new Date(historyStats.last_po.date).toLocaleDateString() : 'N/A'}
                </div>
                <div className="text-xs mt-1">
                  <span className="text-gray-500">{historyStats.last_po.supplier || ''}</span>
                  {historyStats.last_po.status && (
                    <span className={`ml-2 px-1 py-0.5 rounded text-xs ${
                      historyStats.last_po.status === 'completed' ? 'bg-green-100 text-green-700' :
                      historyStats.last_po.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {historyStats.last_po.status}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <div className="text-gray-400 italic">No PO recorded</div>
            )}
          </div>

          {/* Last Stock Adjustment */}
          <div className="bg-white p-3 rounded border">
            <div className="text-xs text-gray-500 font-medium mb-1">LAST STOCK ADJUSTMENT</div>
            {historyStats.last_stock_adjustment ? (
              <>
                <div className="font-semibold">{historyStats.last_stock_adjustment.adjustment_number || 'N/A'}</div>
                <div className="text-gray-600 text-xs">
                  {historyStats.last_stock_adjustment.date ? new Date(historyStats.last_stock_adjustment.date).toLocaleDateString() : 'N/A'}
                </div>
                <div className="text-xs mt-1">
                  <span className="text-gray-500">{historyStats.last_stock_adjustment.type || ''}</span>
                  {historyStats.last_stock_adjustment.quantity_change && (
                    <span className={`ml-2 font-semibold ${historyStats.last_stock_adjustment.quantity_change > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {historyStats.last_stock_adjustment.quantity_change > 0 ? '+' : ''}{historyStats.last_stock_adjustment.quantity_change}
                    </span>
                  )}
                </div>
              </>
            ) : (
              <div className="text-gray-400 italic">No adjustments recorded</div>
            )}
          </div>

          {/* Last Claim */}
          <div className="bg-white p-3 rounded border col-span-2">
            <div className="text-xs text-gray-500 font-medium mb-1">LAST SUPPLIER CLAIM</div>
            {historyStats.last_claim ? (
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-semibold">{historyStats.last_claim.claim_number || 'N/A'}</span>
                  <span className="text-gray-500 ml-2">({historyStats.last_claim.supplier_name || 'N/A'})</span>
                  {historyStats.last_claim.grv_number && (
                    <span className="text-gray-400 ml-1 text-xs">GRV: {historyStats.last_claim.grv_number}</span>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-600">
                    {historyStats.last_claim.date || 'N/A'}
                  </div>
                  <div className="text-xs">
                    <span className="text-red-600 font-semibold">Qty: {historyStats.last_claim.quantity}</span>
                    {historyStats.last_claim.type && (
                      <span className={`ml-1 px-1 py-0.5 rounded text-[10px] ${
                        historyStats.last_claim.movement_type === 'claim_reversal' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {historyStats.last_claim.movement_type === 'claim_reversal' ? 'REVERSED' : historyStats.last_claim.type.replace('_', ' ')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-gray-400 italic">No claims recorded</div>
            )}
          </div>
        </div>
      </div>

      {/* Complete Product Activity Timeline */}
      <div className="bg-white p-4 rounded-lg border border-slate-200" data-testid="product-activity-timeline-panel">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-3">
          <div>
            <h3 className="font-semibold text-slate-800" data-testid="product-activity-timeline-heading">
              Complete Product Activity (Newest First)
            </h3>
            <p className="text-xs text-slate-500" data-testid="product-activity-timeline-subheading">
              Includes sales, GRVs, stock take variances/adjustments, expiry write-offs, transfers, and PO events.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs">
            <div className="px-2 py-1 rounded bg-slate-100" data-testid="product-activity-total-events">
              Total events: <span className="font-semibold">{timelineEvents.length}</span>
            </div>
            <div className="px-2 py-1 rounded bg-emerald-50 text-emerald-700" data-testid="product-activity-stock-events">
              Stock-impact events: <span className="font-semibold">{timelineEvents.filter((e) => e?.stock_impact).length}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
          <div>
            <label className="text-xs text-slate-600" htmlFor="activity-filter">Filter by activity</label>
            <select
              id="activity-filter"
              className="w-full mt-1 border rounded px-2 py-1.5 text-sm"
              value={activityFilter}
              onChange={(e) => setActivityFilter(e.target.value)}
              data-testid="product-activity-filter-select"
            >
              {availableActivityTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-slate-600" htmlFor="activity-search">Search references or notes</label>
            <input
              id="activity-search"
              className="w-full mt-1 border rounded px-2 py-1.5 text-sm"
              placeholder="Search document number, supplier, transaction type..."
              value={activitySearch}
              onChange={(e) => setActivitySearch(e.target.value)}
              data-testid="product-activity-search-input"
            />
          </div>
        </div>

        {filteredTimeline.length === 0 ? (
          <div className="text-sm text-slate-500 py-4" data-testid="product-activity-empty-state">
            No activity events found for the selected filter.
          </div>
        ) : (
          <div className="overflow-auto max-h-[420px] border rounded" data-testid="product-activity-table-wrapper">
            <table className="w-full text-sm" data-testid="product-activity-table">
              <thead className="sticky top-0 bg-slate-100 z-10">
                <tr>
                  <th className="px-2 py-2 text-left">Date/Time</th>
                  <th className="px-2 py-2 text-left">Activity</th>
                  <th className="px-2 py-2 text-left">Reference</th>
                  <th className="px-2 py-2 text-right">Qty Impact</th>
                  <th className="px-2 py-2 text-right">Value</th>
                  <th className="px-2 py-2 text-left">Status</th>
                  <th className="px-2 py-2 text-left">Details</th>
                </tr>
              </thead>
              <tbody>
                {filteredTimeline.map((event, index) => {
                  const qty = Number(event?.quantity_change || 0);
                  const qtyClass = qty > 0 ? "text-emerald-700" : qty < 0 ? "text-red-700" : "text-slate-600";
                  const detailParts = [
                    event?.meta?.transaction_type,
                    event?.meta?.supplier,
                    event?.meta?.customer_name,
                    event?.notes,
                  ].filter(Boolean);

                  return (
                    <tr
                      key={event?.id || `${event?.reference}-${index}`}
                      className={index % 2 === 0 ? "bg-white" : "bg-slate-50"}
                      data-testid={`product-activity-row-${index}`}
                    >
                      <td className="px-2 py-2 whitespace-nowrap" data-testid={`product-activity-date-${index}`}>
                        {formatDateTime(event?.date)}
                      </td>
                      <td className="px-2 py-2">
                        <div className="font-medium" data-testid={`product-activity-type-${index}`}>{event?.event_type || "UNKNOWN"}</div>
                        <div className="text-[11px] text-slate-500">{event?.event_group || "Other"}</div>
                      </td>
                      <td className="px-2 py-2 font-mono text-xs" data-testid={`product-activity-reference-${index}`}>
                        {event?.reference || "-"}
                      </td>
                      <td className={`px-2 py-2 text-right font-semibold ${qtyClass}`} data-testid={`product-activity-qty-${index}`}>
                        {qty > 0 ? "+" : ""}{qty.toFixed(2)}
                      </td>
                      <td className="px-2 py-2 text-right" data-testid={`product-activity-value-${index}`}>
                        {toCurrency(event?.value || 0)}
                      </td>
                      <td className="px-2 py-2" data-testid={`product-activity-status-${index}`}>
                        {event?.status || "-"}
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-600" data-testid={`product-activity-details-${index}`}>
                        {detailParts.join(" • ") || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => fetchHistoryStats(productId)}
          disabled={loadingHistory}
          data-testid="product-history-refresh-button"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loadingHistory ? 'animate-spin' : ''}`} />
          Refresh Stats
        </Button>
      </div>
    </div>
  );
};
